import React, { useEffect, useMemo, useState } from "react";
import { Check, ChevronsUpDown, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import api from "@/lib/api";

/**
 * Searchable combobox over the full Badi biomarker catalog (73 unique markers).
 * - Type-ahead across label + aliases
 * - Picking a marker auto-fills the default unit (US by default)
 * - Multiple unit variants (e.g. ApoB mg/dL vs g/L) shown as sub-options when applicable
 * - "Other (custom)" fallback for unusual markers not in the catalog
 *
 * Props:
 *   value: string  — current marker name (label or free text)
 *   onChange: (markerLabel, suggestedUnit) => void
 */
export default function MarkerCombobox({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [customMode, setCustomMode] = useState(false);
  const [customInput, setCustomInput] = useState("");

  useEffect(() => {
    let cancelled = false;
    api.get("/biomarkers/catalog")
      .then((r) => { if (!cancelled) setCatalog(r.data || []); })
      .catch(() => { /* silent — combobox still works in free-text mode */ });
    return () => { cancelled = true; };
  }, []);

  // The set of common, high-signal markers to feature first
  const featured = useMemo(() => new Set(["Vitamin D, 25-Hydroxy", "hs-CRP", "Hemoglobin A1c", "ApoB", "Ferritin", "TSH", "Fasting Insulin", "Testosterone", "HRV"]), []);
  const { featuredList, allOther } = useMemo(() => {
    const f = [];
    const o = [];
    for (const e of catalog) {
      if (featured.has(e.label)) f.push(e); else o.push(e);
    }
    return { featuredList: f, allOther: o };
  }, [catalog, featured]);

  const pick = (entry) => {
    // Default to first unit (US convention listed first in our catalog)
    const unit = entry.units?.[0]?.unit || "";
    onChange(entry.label, unit);
    setOpen(false);
  };

  const commitCustom = () => {
    const v = customInput.trim();
    if (!v) return;
    onChange(v, ""); // unit blank — user will fill manually
    setCustomMode(false);
    setCustomInput("");
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setCustomMode(false); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          data-testid="marker-combobox-trigger"
          className="w-full flex items-center justify-between bg-transparent border-b border-[#D1C7B8] focus:border-[#C05C42] py-2 outline-none transition-colors text-[#2D322B] text-left"
        >
          <span className={cn(value ? "" : "text-[#9A8F7E]")}>{value || "Search markers…"}</span>
          <ChevronsUpDown className="w-4 h-4 text-[#9A8F7E]" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0 bg-[#F9F6F0] border-[#D1C7B8]" align="start">
        <Command
          filter={(itemValue, search) => {
            // itemValue contains label + aliases joined; case-insensitive substring match
            return itemValue.toLowerCase().includes(search.toLowerCase()) ? 1 : 0;
          }}
        >
          <CommandInput
            placeholder="Search by name or alias (e.g. 'a1c', 'vit d')…"
            data-testid="marker-combobox-search"
            className="text-[#2D322B] placeholder:text-[#9A8F7E]"
          />
          <CommandList className="max-h-[320px]">
            <CommandEmpty>
              <div className="py-2 text-[#5C6359]">
                <p className="mb-2">No marker matches.</p>
                <button
                  type="button"
                  onClick={() => setCustomMode(true)}
                  data-testid="marker-combobox-add-custom"
                  className="text-[#C05C42] text-sm underline"
                >
                  Add it as a custom marker
                </button>
              </div>
            </CommandEmpty>

            {customMode && (
              <div className="px-3 py-3 border-b border-[#D1C7B8] bg-[#F1ECE4]">
                <p className="text-xs text-[#5C6359] mb-2">Custom marker name</p>
                <div className="flex gap-2">
                  <input
                    autoFocus
                    value={customInput}
                    onChange={(e) => setCustomInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commitCustom(); } }}
                    data-testid="marker-combobox-custom-input"
                    className="flex-1 bg-white border border-[#D1C7B8] rounded px-2 py-1 text-sm text-[#2D322B] outline-none focus:border-[#C05C42]"
                    placeholder="e.g. NMR LipoProfile"
                  />
                  <button type="button" onClick={commitCustom} className="text-xs uppercase tracking-widest bg-[#C05C42] text-white px-3 rounded">Add</button>
                </div>
              </div>
            )}

            {featuredList.length > 0 && (
              <CommandGroup heading="Most-tracked">
                {featuredList.map((e) => (
                  <MarkerRow key={e.key} entry={e} selected={value === e.label} onPick={pick} />
                ))}
              </CommandGroup>
            )}
            <CommandGroup heading={featuredList.length > 0 ? "All markers" : undefined}>
              {allOther.map((e) => (
                <MarkerRow key={e.key} entry={e} selected={value === e.label} onPick={pick} />
              ))}
            </CommandGroup>
            <CommandGroup>
              <CommandItem
                value="__add_custom__"
                onSelect={() => setCustomMode(true)}
                data-testid="marker-combobox-add-custom-item"
                className="text-[#C05C42]"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add a custom marker not in this list
              </CommandItem>
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function MarkerRow({ entry, selected, onPick }) {
  // cmdk's filter receives the `value` prop; encode label + aliases for search
  const searchKey = `${entry.label} ${(entry.aliases || []).join(" ")}`;
  const unitLabels = (entry.units || []).map((u) => u.unit).join(" • ");
  return (
    <CommandItem
      value={searchKey}
      onSelect={() => onPick(entry)}
      data-testid={`marker-option-${entry.key}`}
    >
      <Check className={cn("w-4 h-4 mr-2", selected ? "opacity-100 text-[#C05C42]" : "opacity-0")} />
      <span className="flex-1 text-[#2D322B]">{entry.label}</span>
      <span className="text-xs text-[#9A8F7E] ml-2">{unitLabels}</span>
    </CommandItem>
  );
}
